export type Rgba = { r: number; g: number; b: number; a: number };

/** Check if a color string is a CSS variable reference. */
export const isCssVar = (color: string): boolean =>
	typeof color === "string" && color.trimStart().startsWith("var(");

// Memoize resolved CSS variables within a render frame. Cleared on each macrotask
// so theme changes are picked up, but repeated reads during a drag are cheap.
let resolveCache: Map<string, string> | null = null;
const getCache = (): Map<string, string> => {
	if (!resolveCache) {
		resolveCache = new Map();
		if (typeof queueMicrotask !== "undefined") {
			queueMicrotask(() => {
				resolveCache = null;
			});
		}
	}
	return resolveCache;
};

/**
 * Resolve a CSS variable to its computed value, or return the color as-is
 * if it's not a variable.
 *
 * SSR/jsdom safe: when `document` is unavailable, returns the fallback.
 * Results are memoized per microtask to avoid hammering `getComputedStyle`
 * during drags (which would trigger style recalc every pointer move).
 */
export const resolveCssVar = (color: string): string => {
	if (!isCssVar(color)) return color;
	if (typeof document === "undefined" || !document.body) return "#000000";
	const cache = getCache();
	const cached = cache.get(color);
	if (cached !== undefined) return cached;
	const varName = color.match(/\((.*?)\)/)?.[1] || "";
	const value = getComputedStyle(document.body)
		.getPropertyValue(varName)
		.trim();
	const resolved = value || "#000000";
	cache.set(color, resolved);
	return resolved;
};

/**
 * Parse a hex color string to an rgba object. Accepts 3, 4, 6, or 8 char forms,
 * with or without leading '#'. NaN channels fall back to 0 (or 1 for alpha).
 */
export const hexToRgba = (hex: string): Rgba => {
	const h = hex.replace("#", "");

	let r = 0;
	let g = 0;
	let b = 0;
	let a = 1;

	if (h.length === 8) {
		r = Number.parseInt(h.substring(0, 2), 16);
		g = Number.parseInt(h.substring(2, 4), 16);
		b = Number.parseInt(h.substring(4, 6), 16);
		a = Number.parseInt(h.substring(6, 8), 16) / 255;
	} else if (h.length === 6) {
		r = Number.parseInt(h.substring(0, 2), 16);
		g = Number.parseInt(h.substring(2, 4), 16);
		b = Number.parseInt(h.substring(4, 6), 16);
	} else if (h.length === 4) {
		r = Number.parseInt(h.charAt(0) + h.charAt(0), 16);
		g = Number.parseInt(h.charAt(1) + h.charAt(1), 16);
		b = Number.parseInt(h.charAt(2) + h.charAt(2), 16);
		a = Number.parseInt(h.charAt(3) + h.charAt(3), 16) / 255;
	} else if (h.length === 3) {
		r = Number.parseInt(h.charAt(0) + h.charAt(0), 16);
		g = Number.parseInt(h.charAt(1) + h.charAt(1), 16);
		b = Number.parseInt(h.charAt(2) + h.charAt(2), 16);
	}

	r = Number.isNaN(r) ? 0 : r;
	g = Number.isNaN(g) ? 0 : g;
	b = Number.isNaN(b) ? 0 : b;
	a = Number.isNaN(a) ? 1 : a;

	return { r, g, b, a };
};

/**
 * Convert an rgba object to a hex string.
 * - Default: returns 6-char uppercase hex; if `a < 1` and `includeAlpha` is not set,
 *   appends the alpha byte (legacy behavior from the previous single-file picker).
 * - `includeAlpha=true` always appends the alpha byte.
 * - `includeAlpha=false` forces 6-char output even when alpha < 1.
 */
export const rgbaToHex = (rgba: Rgba, includeAlpha?: boolean): string => {
	const r = Math.max(0, Math.min(255, Math.round(rgba.r)));
	const g = Math.max(0, Math.min(255, Math.round(rgba.g)));
	const b = Math.max(0, Math.min(255, Math.round(rgba.b)));
	// Match legacy behavior exactly: lowercase 6-char base, uppercased only when
	// alpha is omitted (opaque). Preserved so persisted customColors[] entries
	// don't drift in case against values produced before this refactor.
	const baseLower = `#${r.toString(16).padStart(2, "0")}${g
		.toString(16)
		.padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;

	const wantAlpha =
		includeAlpha === true || (includeAlpha === undefined && rgba.a < 1);
	if (wantAlpha) {
		const a = Math.max(0, Math.min(255, Math.round(rgba.a * 255)));
		return `${baseLower}${a.toString(16).padStart(2, "0")}`;
	}
	return baseLower.toUpperCase();
};

/**
 * Normalize a hex color:
 * - ensures leading '#'
 * - expands 3-char shorthand to 6-char
 * - expands 4-char shorthand to 8-char RGBA (i.e. 'abcd' → '#AABBCCDD',
 *   interpreting the 4th nibble as alpha — standard CSS hex-with-alpha
 *   shorthand, NOT a malformed 6-char)
 * - uppercases
 */
export const normalizeHexColor = (hex: string): string => {
	let h = hex.startsWith("#") ? hex : `#${hex}`;
	if (h.length === 4) {
		h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
	} else if (h.length === 5) {
		h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}${h[4]}${h[4]}`;
	}
	return h.toUpperCase();
};

/**
 * Format an rgba() CSS string. Alpha is trimmed to at most 2 decimals with
 * trailing zeros dropped (so alpha=1 emits "1" not "1.00").
 */
export const formatRgba = (
	r: number,
	g: number,
	b: number,
	a: number,
): string => {
	const ri = Math.max(0, Math.min(255, Math.round(r)));
	const gi = Math.max(0, Math.min(255, Math.round(g)));
	const bi = Math.max(0, Math.min(255, Math.round(b)));
	const ac = Math.max(0, Math.min(1, a));
	// round to 2 decimals, drop trailing zeros
	const rounded = Math.round(ac * 100) / 100;
	const alphaStr = Number.isInteger(rounded)
		? String(rounded)
		: String(rounded).replace(/\.?0+$/, "");
	return `rgba(${ri}, ${gi}, ${bi}, ${alphaStr})`;
};
